import React from "react";
import { Link } from "react-router-dom";
import {
  Home as HomeIcon, Sun, DoorOpen, Trees, Wind, Waves,
  ShieldCheck, Award, Handshake, Clock, ArrowRight, Star, Phone, Check, Quote,
  Sparkles,
  CloudLightning, Wallet, CalendarCheck, FileCheck2,
  FileCheck, Layers, RefreshCw, Droplets, Languages,
  Mail,
  MapPin,
  ClipboardCheck,
  FileText,
  Receipt,
  MonitorSmartphone,
} from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import TrustStrip from "../components/TrustStrip";
import BrandLogo from "../components/BrandLogo";
import ProjectStrip from "../components/ProjectStrip";
import HeroMedia from "../components/HeroMedia";
import WarrantyBadge from "../components/WarrantyBadge";
import {
  brand, services, values, testimonials,
  whyChoose, promo, howItWorks, warranty, inspection, paperwork, financing,
  productBrands, lifecyclePlan, remodeling,
} from "../data/site";

const paperIcons = { ClipboardCheck, FileText, CloudLightning, FileCheck2, ShieldCheck, Receipt };

const iconMap = { Home: HomeIcon, MonitorSmartphone, Sun, Sparkles, FileCheck, Layers, RefreshCw, Droplets, ShieldCheck, Award, Handshake, Clock, Languages };

const Home = () => {

  return (
    <div>
      <SEO
        title="Roof Repair & Replacement in Phoenix, AZ | Pepla Roofing"
        description="Residential roofing in Phoenix and Maricopa County — roof repair, roof replacement, tile underlayment and free roof inspections for homeowners. Tile, shingle, flat and foam roofs."
        alternate={{ en: `${SITE_URL}/`, es: `${SITE_URL}/espanol` }}
      />

      {/* HERO */}
      {/* Phones stack: copy on ink, then the photo complete underneath — the
          wrap artwork is the point of this shot and a full-height cover crop
          on a portrait screen cuts the trailer off entirely. Tablet and up it
          goes back to a full-bleed background behind the copy. */}
      <section className="relative flex flex-col lg:block lg:min-h-[100svh] bg-ink">
        <div className="relative order-2 w-full lg:absolute lg:inset-0 lg:order-none">
          <HeroMedia alt="Pepla Roofing truck and equipment trailer at a Phoenix home" />
          <div className="hidden lg:block absolute inset-0 hero-overlay" />
        </div>
        <div className="relative order-1 lg:order-none max-w-7xl mx-auto px-5 lg:px-8 w-full pt-28 pb-10 lg:pb-16 lg:min-h-[100svh] lg:flex lg:items-center">
          <div className="max-w-2xl fade-up">
            {/* Gated on the license, like every other remodeling mention on
                the site. Naming remodeling in the hero is offering the work,
                and A.R.S. § 32-1151 makes offering it without the B
                classification the same violation as doing it. */}
            {/* nowrap halves so that when this wraps on a narrow phone it
                breaks at the dot rather than orphaning "AZ" on its own line. */}
            <span className="block text-white/70 text-xs font-semibold tracking-[0.25em] uppercase mb-5">
              <span className="whitespace-nowrap">
                {remodeling.enabled ? "Roofing & Remodeling" : "Roofing"}
              </span>{" "}
              · <span className="whitespace-nowrap">Phoenix, AZ</span>
            </span>
            <h1 className="font-display text-white text-4xl sm:text-5xl md:text-6xl uppercase mb-6">
              Built to Rise.<br />
              <span className="brand-gradient-text">Built to Protect.</span>
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-8 max-w-xl">
              Tile, shingle, flat and foam roofs across the Valley — repaired, replaced and inspected by a crew that tells you what your roof actually needs.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors flex items-center gap-2"
              >
                Get a Free Estimate <ArrowRight size={18} />
              </Link>
              <a
                href={`tel:${brand.phoneRaw}`}
                className="border border-white/30 hover:border-brand text-white hover:text-brand font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors flex items-center gap-2 backdrop-blur-sm"
              >
                <Phone size={18} /> {brand.phone}
              </a>
            </div>
            <TrustStrip className="mt-10" />
          </div>

        </div>
      </section>

      {/* PROMO — the offer shown as a line on an estimate */}
      {promo.enabled && (
        <section className="promo-band relative overflow-hidden border-y border-brand/25 text-white">
          <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-16 md:py-20 grid lg:grid-cols-[minmax(0,420px)_1fr] gap-12 lg:gap-16 items-center">
            {/* estimate card */}
            <figure className="promo-quote rounded-xl overflow-hidden shadow-2xl">
              <figcaption className="flex items-baseline justify-between gap-4 px-6 py-4 border-b border-ink/10">
                <span className="font-heading text-sm uppercase tracking-wider text-ink">{promo.quote.title}</span>
                {brand.rocNumber && <span className="text-ink/60 text-[11px]">ROC #{brand.rocNumber}</span>}
              </figcaption>
              <div className="px-6 py-2">
                {promo.quote.lines.map((l) => (
                  <div key={l.label} className="flex items-baseline justify-between gap-6 py-3 border-b border-dashed border-ink/10 last:border-b-0">
                    <span className="text-ink/70 text-sm">{l.label}</span>
                    <span className="text-ink/60 text-sm tabular-nums">{l.value}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-baseline justify-between gap-6 bg-brand px-6 py-4">
                <span className="font-heading text-lg uppercase tracking-wide text-ink">{promo.quote.upgradeLabel}</span>
                <span className="font-heading text-3xl text-ink tabular-nums leading-none">{promo.quote.upgradeValue}</span>
              </div>
              <div className="px-6 py-3 bg-ink/[0.03]">
                <span className="text-ink/60 text-[11px]">{promo.quote.footnote}</span>
              </div>
            </figure>

            {/* offer */}
            <div>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.98] tracking-tight mb-6">
                {promo.headline}
              </h2>
              <p className="text-white/70 text-lg leading-relaxed max-w-xl mb-9">{promo.text}</p>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <a
                  href={`tel:${brand.phoneRaw}`}
                  className="inline-flex items-center gap-3 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-5 rounded-md transition-colors"
                >
                  <Phone size={20} /> {brand.phone}
                </a>
                <span className="text-white/45 text-sm">{promo.note}</span>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* WORKMANSHIP WARRANTY — moved up here from "Backed In Writing".
          It is the strongest claim on the page and it was sitting eight
          sections down, after products, services, about and how-it-works. */}
      <section className="py-20 bg-background">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          {warranty.laborYears && (
            <div className="bg-ink text-white rounded-2xl overflow-hidden">
              <div className="grid md:grid-cols-[auto_1fr] gap-8 md:gap-10 items-center p-8 md:p-10">
                <div className="flex justify-center md:block">
                  <WarrantyBadge size={208} className="w-44 md:w-52" />
                </div>
                <div className="text-center md:text-left">
                  <span className="block text-brand text-xs font-semibold tracking-[0.22em] uppercase mb-3">
                    Our own labor warranty
                  </span>
                  <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-tight leading-[1.05] mb-4">
                    {warranty.laborYears} Years On Our Workmanship
                  </h2>
                  <p className="text-white/70 leading-relaxed mb-6 max-w-xl">
                    If your roof leaks because of how we installed it, we come back and fix it —
                    labor and materials, at no charge to you. The term is written into your
                    contract before work starts, not promised on a handshake.
                  </p>
                  <ul className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-2.5 mb-7">
                    {[
                      `Non-prorated — year ${warranty.laborYears} is covered like year one`,
                      "No deductible, no service call fee",
                      "Transfers to the next owner once, free",
                    ].map((t) => (
                      <li key={t} className="inline-flex items-center gap-2 text-white/80 text-sm">
                        <Check size={14} className="text-brand shrink-0" aria-hidden="true" />
                        {t}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/warranty-claim"
                    className="inline-flex items-center gap-2 text-brand hover:text-brand-light font-semibold uppercase tracking-wider text-sm transition-colors"
                  >
                    What it covers, and how to claim <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* PRODUCTS */}
      <section className="py-20 bg-secondary border-b border-ink/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <span className="flex items-center gap-4 kicker">
              <span className="w-10 h-px bg-brand" /> Products
            </span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-4 mb-4">What Goes On Your Roof</h2>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground text-lg leading-relaxed">
              The manufacturers whose products we install. Every product on your estimate is named by manufacturer,
              product line and color before you sign, never just "architectural shingle" and a price.
            </p>
          </div>
          {/* A wrapping grid, not a single nowrap row.
              The previous layout forced every logo onto one line and shrank
              --logo-h to 1.5rem to make them fit; at 11 brands that was tight
              and at 16 it was unreadable. Eight columns from lg up puts them
              on two even rows and lets each mark render at a size someone can
              actually recognize, and any future additions just wrap onto a
              third row instead of squeezing the rest. */}
          <ul
            className="mt-12 grid grid-cols-3 items-center justify-items-center gap-x-6 gap-y-9 [--logo-h:2.25rem] sm:grid-cols-4 md:grid-cols-6 md:gap-x-8 md:[--logo-h:2.4rem] lg:grid-cols-8 lg:gap-x-8 lg:gap-y-10 lg:[--logo-h:2.5rem] xl:gap-x-10 xl:[--logo-h:2.75rem]"
            aria-label="Roofing manufacturers we install"
          >
            {productBrands.map((b) => (
              <li key={b.file} className="flex items-center justify-center min-w-0 md:shrink">
                <BrandLogo brand={b} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">What We Do</h2>
            <div className="brand-line mb-5" />
            <p className="text-muted-foreground text-lg">
              One trade, done properly. Tile, shingle, flat and foam roofs — repaired, replaced, re-felted and inspected across the Valley.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            {services.filter((s) => !s.secondary).map((s) => {
              const Icon = iconMap[s.icon];
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group card-lift bg-card border border-ink/10 rounded-2xl overflow-hidden flex flex-col"
                >
                  <div className="img-zoom h-52 relative">
                    <img loading="lazy" decoding="async" src={s.hero} alt={s.name} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
                    <div className="absolute top-4 left-4 w-12 h-12 rounded-xl bg-brand flex items-center justify-center">
                      <Icon size={22} className="text-ink" />
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-heading text-2xl uppercase tracking-wide mb-2 group-hover:text-brand transition-colors">{s.name}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed flex-1">{s.short}</p>
                    <span className="inline-flex items-center gap-2 text-brand-dark font-semibold text-sm mt-4 uppercase tracking-wide">
                      Learn More <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* LIFECYCLE PLAN — the signature offering.
          Placed directly after the services grid on purpose: the reader has
          just seen the four things every roofer in the Valley does, and this
          is the one thing only Pepla does. It gets a full band rather than a
          card in a row, because a product nobody else sells is the only real
          defence a small shop has against a competitor with 800 reviews. */}
      <section className="py-24 bg-secondary border-y border-ink/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="kicker">Only from Pepla</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
              {lifecyclePlan.name}
            </h2>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              {lifecyclePlan.tagline}
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Scheduled inspections, priority service when the monsoon hits, minor repairs
              handled on the visit, and a documented roof history that answers a buyer's
              questions when you sell.
            </p>
            <Link
              to="/lifecycle-plan"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
            >
              See How It Works <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {lifecyclePlan.included.map((item) => (
              <div key={item.title} className="bg-card border border-ink/10 rounded-2xl p-6">
                <h3 className="font-heading text-lg uppercase tracking-wide mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-24 section-dark text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="rounded-2xl w-full aspect-[3/2] bg-white/5 border border-white/10 shadow-2xl flex items-center justify-center p-10">
              <img loading="lazy" decoding="async" src={brand.logo} alt={brand.name} width="820" height="814" className="w-full max-w-xs h-auto object-contain" />
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
              <span className="inline-flex items-center gap-2 bg-brand text-ink font-semibold uppercase tracking-wider text-xs px-3.5 py-2 rounded-md">
                Free Inspections
              </span>
              <span className="text-white/70">{inspection.hours}</span>
              {brand.yearsExperience && (
                <span className="text-white/70">· {brand.yearsExperience} years of roofing experience</span>
              )}
            </div>
          </div>
          <div>
            <span className="kicker-dark">About Pepla</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">
              The Transparency You Need
            </h2>
            <div className="brand-line mb-6" />
            <p className="text-white/70 leading-relaxed mb-8">
              On a Valley tile roof, the tile usually outlives the felt underneath it. So when a tile roof leaks, the honest answer is often new underlayment and your existing tile reset — not a tear-off, and not the number on someone else's bid. We inspect for free, tell you which one your roof actually needs, and put every product on the estimate by manufacturer, line and color.
            </p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 mb-8">
              {values.map((v) => (
                <div key={v} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-brand/15 flex items-center justify-center shrink-0">
                    <Check size={14} className="text-brand" />
                  </span>
                  <span className="text-sm text-white/85">{v}</span>
                </div>
              ))}
            </div>
            <blockquote className="border-l-2 border-brand pl-5 italic text-white/80 mb-8">
              "We tell you what your roof actually needs — not what's easiest to sell. Then we put it in writing before anyone gets on the roof."
            </blockquote>
            <Link to="/about" className="inline-flex items-center gap-2 text-brand font-semibold uppercase tracking-wide text-sm link-underline">
              Learn More About Us <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="kicker">Why Pepla</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">The Pepla Difference</h2>
            <div className="brand-line mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((w) => {
              const Icon = iconMap[w.icon] || Check;
              return (
                <div key={w.title} className="card-lift bg-card border border-ink/10 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-ink flex items-center justify-center mx-auto mb-5">
                    <Icon size={28} className="text-brand" />
                  </div>
                  <h3 className="font-heading text-xl uppercase tracking-wide mb-2">{w.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{w.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* WHERE TO START
          Was three loose cards — one dark, two light, uneven heights, actions
          floating at different depths — with the scheduling note dumped
          underneath as a full-width run of grey text. It is one plate now,
          divided by hairlines, with the note as a footer strip: the shape of
          a printed form, which is what this company sells. Hierarchy comes
          from the one solid button, not from one card being a different
          colour. */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-12">
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">Where To Start</h2>
            <div className="brand-line mb-5" />
            <p className="text-muted-foreground text-lg leading-relaxed">
              A free look at the roof, help with a carrier, or a way to pay for it — whichever
              one you need first.
            </p>
          </div>

          {/* Hairlines are the 1px grid gap showing the wrapper through, so
              they land correctly however the grid reflows. */}
          <div className="rounded-2xl overflow-hidden border border-ink/10 bg-ink/10">
            <div className="grid md:grid-cols-3 gap-px">
              <div className="bg-card p-8 flex flex-col">
                <CalendarCheck size={24} className="text-brand-dark mb-5" aria-hidden="true" />
                <h3 className="font-heading text-2xl uppercase tracking-wide mb-3">Free Roof Inspection</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-7">
                  {inspection.sameDay
                    ? `We inspect ${inspection.hours}, and same-day is often available. Request a time and we call to confirm it. Free, no obligation, and you keep the photos.`
                    : "Free, no obligation, and you keep the photos — whether you hire us or not."}
                </p>
                <a
                  href="#quote"
                  className="mt-auto inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-xs px-6 py-3.5 rounded-md transition-colors self-start"
                >
                  Book an inspection <ArrowRight size={15} />
                </a>
              </div>

              <div className="bg-card p-8 flex flex-col">
                <CloudLightning size={24} className="text-brand-dark mb-5" aria-hidden="true" />
                <h3 className="font-heading text-2xl uppercase tracking-wide mb-3">Claim Denied?</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-7">
                  Monsoon and hail damage isn't always visible from the ground. We inspect, photograph the damage and
                  write a scope your carrier can work from — even after a denial.
                </p>
                <Link
                  to="/insurance-claims"
                  className="mt-auto inline-flex items-center gap-2 text-brand-dark font-semibold uppercase tracking-wider text-xs link-underline self-start"
                >
                  Storm &amp; insurance help <ArrowRight size={15} />
                </Link>
              </div>

              <div className="bg-card p-8 flex flex-col">
                <Wallet size={24} className="text-brand-dark mb-5" aria-hidden="true" />
                <h3 className="font-heading text-2xl uppercase tracking-wide mb-3">Financing Available</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-7">
                  A failing roof doesn't wait for the budget. Get the written price first, then talk through payment
                  options — we offer financing through {financing.partnerName || "our lending partner"} for qualifying projects.
                </p>
                <Link
                  to="/financing"
                  className="mt-auto inline-flex items-center gap-2 text-brand-dark font-semibold uppercase tracking-wider text-xs link-underline self-start"
                >
                  See financing <ArrowRight size={15} />
                </Link>
              </div>
            </div>

            {inspection.sameDay && inspection.note && (
              <div className="bg-card px-8 py-5 mt-px">
                <p className="text-muted-foreground text-xs leading-relaxed max-w-3xl">{inspection.note}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 section-dark text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Five Steps, No Runaround</h2>
            <div className="brand-line mx-auto" />
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {howItWorks.map((st, i) => (
              <li key={st.title} className="bg-white/5 border border-white/10 rounded-2xl p-6 relative">
                <span aria-hidden="true" className="font-heading text-5xl text-brand/25 leading-none absolute top-4 right-5">{i + 1}</span>
                <h3 className="font-heading text-lg uppercase tracking-wide mb-2 pr-8">{st.title}</h3>
                <p className="text-white/65 text-sm leading-relaxed">{st.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PAPERWORK — what the customer ends up holding. The warranty panel
          that used to lead this section now sits above the products, near
          the top of the page. */}
      <section className="py-24 bg-secondary">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">Backed In Writing</h2>
          <div className="brand-line mb-5" />
          <p className="text-muted-foreground text-lg leading-relaxed max-w-xl mb-10">
            Every job leaves a paper trail you keep. This is what ends up in it.
          </p>

          {/* Set as one sheet with ruled divisions rather than six floating
              cards: it is a list of documents, and reading it as an index of
              a file is both truer to the content and the only way six items
              of different lengths line up. */}
          <div className="rounded-2xl overflow-hidden border border-ink/10 bg-ink/10">
            <div className="grid sm:grid-cols-2 gap-px">
              {paperwork.map((d) => {
                const DIcon = paperIcons[d.icon] || FileCheck2;
                const title =
                  d.icon === "FileCheck2" && warranty.materialYears
                    ? `Up to ${warranty.materialYears}-Year Material Warranty`
                    : d.icon === "ShieldCheck" && warranty.laborYears
                    ? `${warranty.laborYears}-Year Workmanship Warranty`
                    : d.title;
                return (
                  <div key={d.title} className="bg-card flex gap-5 p-7 sm:p-8">
                    <span className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0 mt-0.5">
                      <DIcon size={18} className="text-brand-dark" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-heading text-lg uppercase tracking-wide mb-1.5">{title}</h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">{d.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — hidden until Pepla has its own reviews.
          Fill `testimonials` in src/data/site.js (and brand.googleReviewUrl
          for the Google badge) and this section comes back. */}
      {testimonials.length > 0 && (
        <section className="py-24 bg-secondary">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <span className="kicker">Client Reviews</span>
              <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">What Arizona Homeowners Say</h2>
              <div className="brand-line mx-auto mb-8" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.name} className="card-lift bg-card border border-ink/10 rounded-2xl p-7 relative">
                  <Quote size={32} className="text-brand/20 absolute top-5 right-6" />
                  <div className="flex gap-1 mb-4">
                    {[...Array(t.rating)].map((_, i) => <Star key={i} size={16} className="fill-brand text-brand" />)}
                  </div>
                  <p className="text-ink/80 text-sm leading-relaxed mb-6">{t.text}</p>
                  <div className="border-t border-ink/10 pt-4">
                    <div className="font-heading text-lg uppercase tracking-wide">{t.name}</div>
                    <div className="text-muted-foreground text-xs">{t.service} · {t.location}</div>
                  </div>
                </div>
              ))}
            </div>
            {brand.googleReviewUrl && (
              <div className="text-center mt-12">
                <a
                  href={brand.googleReviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-8 py-4 rounded-md transition-colors"
                >
                  Read Our Reviews <ArrowRight size={18} />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* RECENT WORK */}
      <ProjectStrip />

      {/* CONTACT CTA */}
      <section id="quote" className="py-24 section-dark text-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <span className="kicker-dark">Get In Touch</span>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mt-3 mb-4">Ready For Your Next Project?</h2>
            <div className="brand-line mb-6" />
            <p className="text-white/70 leading-relaxed mb-10">
              Tell us what's going on with the roof. Inspections and estimates are free, seven days a week, and
              a team member calls to confirm the time before anyone comes out.
            </p>
            <div className="space-y-6">
              <a href={`tel:${brand.phoneRaw}`} className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center"><Phone size={20} className="text-ink" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Call Us</div>
                  <div className="font-heading text-xl group-hover:text-brand transition-colors">{brand.phone}</div>
                </div>
              </a>
              <a href={`mailto:${brand.emailRaw || brand.email}`} className="flex items-center gap-4 group">
                <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center"><Mail size={20} className="text-ink" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Email Us</div>
                  <div className="text-white/85 group-hover:text-brand transition-colors">{brand.email}</div>
                </div>
              </a>
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center"><MapPin size={20} className="text-ink" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Where We Work</div>
                  <div className="text-white/85">{brand.address || "Phoenix & every city in Maricopa County"}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center"><Clock size={20} className="text-ink" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50">Inspections</div>
                  <div className="text-white/85">{inspection.hours}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
            <LeadForm dark />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
